import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Select from "react-select";
import flatpickr from "flatpickr";
import { Spanish } from "flatpickr/dist/l10n/es.js";
import "flatpickr/dist/themes/material_blue.css";
import moment from "moment-timezone";
import Header from "../components/Header";
import PhoneLoader from "../components/loader/PhoneLoader";

import "../Warranties.css";

import { useAuth } from "../provider/authProvider";

const TZ = "America/Lima";

const WARRANTY_REASON_OPTIONS = [
    { value: "screen_failure", label: "Fallo en pantalla" },
    { value: "touch_failure", label: "Fallo táctil" },
    { value: "camera_failure", label: "Fallo en cámara" },
    { value: "speaker_failure", label: "Fallo en parlante / sonido" },
    { value: "microphone_failure", label: "Fallo en micrófono" },
    { value: "charging_failure", label: "No carga / falla en puerto de carga" },
    { value: "battery_failure", label: "Batería se descarga rápido" },
    { value: "signal_failure", label: "Falla de señal / red móvil" },
    { value: "wifi_bluetooth_failure", label: "Falla WiFi / Bluetooth" },
    { value: "software_failure", label: "Falla de software / sistema" },
    { value: "imei_issue", label: "Problema con IMEI / lectura de red" },
    { value: "overheating", label: "Sobrecalentamiento" },
    { value: "does_not_turn_on", label: "No enciende" },
    { value: "factory_defect", label: "Posible defecto de fábrica" },
    { value: "buttons_failure", label: "Fallo en botones físicos" },
    { value: "fingerprint_failure", label: "Fallo en lector de huella" },
    { value: "face_id_failure", label: "Fallo en reconocimiento facial" },
];

const STATUS_OPTIONS = [
    { value: "0", label: "Todos" },
    { value: "1", label: "Pendiente de evaluación" },
    { value: "2", label: "En diagnóstico" },
    { value: "3", label: "Solucionado" },
    { value: "4", label: "Cambio de equipo" },
    { value: "5", label: "Devolución aprobada" },
    { value: "6", label: "Garantía rechazada" },
    { value: "7", label: "Cerrado" },
];

const statusClass = (status) =>
    ({
        1: "pending",
        2: "diagnostic",
        3: "solved",
        4: "exchange",
        5: "refund",
        6: "rejected",
        7: "closed",
    })[Number(status)] || "pending";

const formatMoney = (value) => `S/ ${Number(value || 0).toFixed(2)}`;

const formatDate = (value) =>
    value ? moment(value).tz(TZ).format("DD/MM/YYYY HH:mm") : "-";

export default function Warranties(props) {
    const { rol, sedeLocation: ctxSedeLocation } = useAuth();

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const token = localStorage.getItem("token");

    const rangeRef = useRef(null);
    const estimatedDateRef = useRef(null);
    const rangeInstanceRef = useRef(null);
    const estimatedInstanceRef = useRef(null);
    const searchTimer = useRef(null);

    const auth = useMemo(
        () => ({ headers: { Authorization: `Bearer ${token}` } }),
        [token],
    );

    const [loading, setLoading] = useState(false);
    const [warranties, setWarranties] = useState([]);
    const [kpis, setKpis] = useState({ total: 0, active: 0, closed: 0 });
    const [advisors, setAdvisors] = useState([]);

    const [search, setSearch] = useState("");
    const [advisorFilter, setAdvisorFilter] = useState(null);
    const [statusFilter, setStatusFilter] = useState(STATUS_OPTIONS[0]);
    const [dateRange, setDateRange] = useState([]);

    const [showModal, setShowModal] = useState(false);
    const [dniSearch, setDniSearch] = useState("");
    const [saleLoading, setSaleLoading] = useState(false);
    const [salesFound, setSalesFound] = useState([]);
    const [selectedSale, setSelectedSale] = useState(null);
    const [selectedReasons, setSelectedReasons] = useState([]);
    const [description, setDescription] = useState("");
    const [estimatedDate, setEstimatedDate] = useState([]);
    const [saving, setSaving] = useState(false);

    const [selectedWarranty, setSelectedWarranty] = useState(null);
    const [statusModal, setStatusModal] = useState(false);
    const [newStatus, setNewStatus] = useState(null);
    const [statusNote, setStatusNote] = useState("");
    const [refundAmount, setRefundAmount] = useState("");
    const [exchangePhone, setExchangePhone] = useState("");
    const [exchangePhoneOption, setExchangePhoneOption] = useState(null);
    const [exchangeImei, setExchangeImei] = useState("");
    const [exchangePrice, setExchangePrice] = useState("");
    const [phoneOptions, setPhoneOptions] = useState([]);

    const advisorOptions = useMemo(
        () => [
            { value: "0", label: "Todos los asesores con garantías" },
            ...advisors.map((a) => ({
                value: String(a.id),
                label: `${a.fullName} (${a.total})`,
            })),
        ],
        [advisors],
    );

    const phoneSelectOptions = useMemo(() => phoneOptions, [phoneOptions]);

    useEffect(() => {
        if (!rangeRef.current) return;

        rangeInstanceRef.current = flatpickr(rangeRef.current, {
            locale: Spanish,
            mode: "range",
            dateFormat: "Y-m-d",
            closeOnSelect: false,
            onChange: (dates) => setDateRange(dates || []),
        });

        return () => {
            if (rangeInstanceRef.current) {
                rangeInstanceRef.current.destroy();
                rangeInstanceRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (!showModal || !estimatedDateRef.current) return;

        if (estimatedInstanceRef.current) {
            estimatedInstanceRef.current.destroy();
            estimatedInstanceRef.current = null;
        }

        estimatedInstanceRef.current = flatpickr(estimatedDateRef.current, {
            locale: Spanish,
            dateFormat: "Y-m-d",
            minDate: "today",
            onChange: (dates) => setEstimatedDate(dates || []),
        });

        return () => {
            if (estimatedInstanceRef.current) {
                estimatedInstanceRef.current.destroy();
                estimatedInstanceRef.current = null;
            }
        };
    }, [showModal, selectedSale]);

    const fetchAdvisors = async () => {
        try {
            const { data } = await axios.get(
                process.env.REACT_APP_API + "/warranties/advisors",
                auth,
            );

            if (data?.msg === "success") setAdvisors(data.advisors || []);
        } catch (error) {
            console.error("fetchAdvisors", error);
        }
    };

    const normalizePhoneOptions = (phones = []) => {
        return (Array.isArray(phones) ? phones : [])
            .map((phone) => {
                const value = phone.value ?? phone.fr_id ?? phone.id ?? phone.idNumber;
                const brand = String(
                    phone.brand ?? phone.fr_brand ?? phone.marca ?? "",
                ).trim();
                const model = String(
                    phone.model ?? phone.fr_model ?? phone.modelo ?? "",
                ).trim();
                const ram = String(phone.ram ?? phone.fr_ram ?? "").trim();
                const rom = String(phone.rom ?? phone.fr_rom ?? "").trim();
                const price = phone.price ?? phone.fr_price ?? phone.precio ?? "";
                const label = String(
                    phone.label ||
                    [brand, model].filter(Boolean).join(" ") +
                    (ram || rom ? ` - ${ram} RAM + ${rom} ROM` : ""),
                )
                    .replace(/\s+-\s+-\s+/g, " - ")
                    .replace(/\s+/g, " ")
                    .trim();

                if (!value || !label) return null;

                return {
                    value: String(value),
                    label,
                    price,
                    brand,
                    model,
                    ram,
                    rom,
                };
            })
            .filter(Boolean);
    };

    const fetchPhones = async () => {
        const apiBase = process.env.REACT_APP_API;

        try {
            const { data } = await axios.get(`${apiBase}/warranties/phones`, auth);

            if (data?.msg === "success") {
                const normalized = normalizePhoneOptions(
                    data.phones || data.data || [],
                );
                setPhoneOptions(normalized);
                return normalized;
            }
        } catch (error) {
            console.warn(
                "fetchPhones /warranties/phones",
                error?.response?.data || error,
            );
        }

        try {
            const { data } = await axios.get(`${apiBase}/cards/meta`, {
                ...auth,
                params: {
                    start: moment().tz(TZ).format("YYYY-MM-DD"),
                    end: moment().tz(TZ).format("YYYY-MM-DD"),
                    sale_state: "all",
                    advisor_id: "0",
                    manager_id: "0",
                    q: "",
                },
            });

            if (data?.msg === "success") {
                const normalized = normalizePhoneOptions(data.phones || []);
                setPhoneOptions(normalized);
                return normalized;
            }
        } catch (error) {
            console.error("fetchPhones /cards/meta", error?.response?.data || error);
        }

        setPhoneOptions([]);
        return [];
    };

    const fetchWarranties = async () => {
        try {
            setLoading(true);

            const params = {
                search: search.trim(),
                advisorID: advisorFilter?.value || "",
                status: statusFilter?.value || "0",
            };

            if (dateRange?.[0]) {
                params.fechaInicio = moment(dateRange[0]).format("YYYY-MM-DD");
            }

            if (dateRange?.[1]) {
                params.fechaFin = moment(dateRange[1]).format("YYYY-MM-DD");
            }

            const { data } = await axios.get(
                process.env.REACT_APP_API + "/warranties",
                {
                    ...auth,
                    params,
                },
            );

            if (data?.msg === "success") {
                setWarranties(data.warranties || []);
                setKpis(data.kpis || { total: 0, active: 0, closed: 0 });
            }
        } catch (error) {
            console.error("fetchWarranties", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAdvisors();
        fetchPhones();
    }, []);

    useEffect(() => {
        if (searchTimer.current) clearTimeout(searchTimer.current);

        searchTimer.current = setTimeout(() => {
            fetchWarranties();
        }, 350);

        return () => clearTimeout(searchTimer.current);
    }, [search, advisorFilter, statusFilter, dateRange]);

    const cleanModal = () => {
        setDniSearch("");
        setSalesFound([]);
        setSelectedSale(null);
        setSelectedReasons([]);
        setDescription("");
        setEstimatedDate([]);

        if (estimatedInstanceRef.current) {
            estimatedInstanceRef.current.clear();
        }
    };

    const openModal = () => {
        cleanModal();
        setShowModal(true);
    };

    const searchSaleByDni = async () => {
        const dni = dniSearch.trim();

        if (!/^\d{8,12}$/.test(dni)) {
            alert("Ingrese un DNI válido.");
            return;
        }

        try {
            setSaleLoading(true);
            setSelectedSale(null);

            const { data } = await axios.get(
                process.env.REACT_APP_API + "/warranties/search-sale",
                {
                    ...auth,
                    params: { dni },
                },
            );

            if (data?.msg === "success") setSalesFound(data.sales || []);
        } catch (error) {
            alert(error?.response?.data?.error || "No se pudo buscar la venta.");
        } finally {
            setSaleLoading(false);
        }
    };

    const saveWarranty = async () => {
        if (!selectedSale?.registerID) {
            alert("Seleccione una venta.");
            return;
        }

        if (!selectedReasons.length) {
            alert("Seleccione al menos una razón de garantía.");
            return;
        }

        try {
            setSaving(true);

            const payload = {
                registerID: selectedSale.registerID,
                reasons: selectedReasons,
                description,
                estimatedDate: estimatedDate?.[0]
                    ? moment(estimatedDate[0]).format("YYYY-MM-DD")
                    : null,
            };

            const { data } = await axios.post(
                process.env.REACT_APP_API + "/warranty/add",
                payload,
                auth,
            );

            if (data?.msg === "success") {
                setShowModal(false);
                await fetchWarranties();
                await fetchAdvisors();

                if (data.pdfFile)
                    window.open(process.env.REACT_APP_API + "/" + data.pdfFile, "_blank");
            }
        } catch (error) {
            alert(error?.response?.data?.error || "No se pudo guardar la garantía.");
        } finally {
            setSaving(false);
        }
    };

    const openStatusModal = async (warranty) => {
        setSelectedWarranty(warranty);

        const selectedStatus =
            STATUS_OPTIONS.find(
                (it) => String(it.value) === String(warranty.status),
            ) || null;

        setNewStatus(selectedStatus);
        setStatusNote("");
        setRefundAmount(warranty.refund_amount || "");

        let currentPhoneOptions = phoneSelectOptions;
        if (String(selectedStatus?.value) === "4" && !currentPhoneOptions.length) {
            currentPhoneOptions = await fetchPhones();
        }

        const replacementLabel = String(
            warranty.replacement_phone_name || "",
        ).trim();
        const matchedPhone = currentPhoneOptions.find(
            (option) => String(option.label).trim() === replacementLabel,
        );

        setExchangePhone(replacementLabel);
        setExchangePhoneOption(matchedPhone || null);
        setExchangeImei(warranty.replacement_imei || "");
        setExchangePrice(
            warranty.replacement_phone_price || matchedPhone?.price || "",
        );
        setStatusModal(true);
    };

    const updateStatus = async () => {
        if (!selectedWarranty?.id || !newStatus?.value || newStatus.value === "0") {
            return;
        }

        try {
            setSaving(true);

            if (String(newStatus.value) === "4") {
                await axios.post(
                    process.env.REACT_APP_API +
                    `/warranty/${selectedWarranty.id}/exchange`,
                    {
                        newPhone: exchangePhoneOption?.label || exchangePhone,
                        newPhoneId: exchangePhoneOption?.value || null,
                        newImei: exchangeImei,
                        newPhonePrice: exchangePrice || exchangePhoneOption?.price || "",
                        note: statusNote,
                    },
                    auth,
                );
            } else {
                await axios.post(
                    process.env.REACT_APP_API + `/warranty/${selectedWarranty.id}/status`,
                    {
                        status: newStatus.value,
                        statusNote,
                        refundAmount,
                    },
                    auth,
                );
            }

            setStatusModal(false);
            await fetchWarranties();
            await fetchAdvisors();
        } catch (error) {
            alert(error?.response?.data?.error || "No se pudo actualizar el estado.");
        } finally {
            setSaving(false);
        }
    };

    useEffect(() => {
        if (
            !statusModal ||
            String(newStatus?.value) !== "4" ||
            phoneSelectOptions.length
        )
            return;
        fetchPhones();
    }, [statusModal, newStatus, phoneSelectOptions.length]);

    const regeneratePdf = async (warranty) => {
        try {
            const { data } = await axios.post(
                process.env.REACT_APP_API + `/warranty/${warranty.id}/regenerate-pdf`,
                {},
                auth,
            );

            if (data?.pdfFile) window.open(data.pdfFile, "_blank");

            await fetchWarranties();
        } catch (error) {
            alert("No se pudo generar el PDF.");
        }
    };

    const kpiCards = [
        { key: "total", label: "Total garantías", value: kpis.total || 0 },
        { key: "active", label: "Vigentes", value: kpis.active || 0 },
        { key: "closed", label: "Cerradas / resueltas", value: kpis.closed || 0 },
        { key: "pending", label: "Pendiente evaluación", value: kpis["1"] || 0 },
    ];

    return (
        <div>
            {loading && <PhoneLoader />}
            <Header user={user} namePage={props.namePage || "Garantías"} />

            <section className="sectionBody sectionBodyWarranty">
                <div className="containerCustom containerBody">
                    <div className="warrantyHero blockBody">
                        <div>
                            <p className="warrantyEyebrow">Servicio Técnico y Garantías</p>
                            <h1>Control de garantías de celulares</h1>
                            <p>
                                Registra equipos en evaluación, controla estados, genera
                                constancias PDF y filtra por cliente o asesor.
                            </p>
                        </div>

                        <button
                            className="warrantyPrimaryBtn"
                            type="button"
                            onClick={openModal}
                        >
                            + Nueva garantía
                        </button>
                    </div>

                    <div className="warrantyKpiGrid">
                        {kpiCards.map((item) => (
                            <article className="warrantyKpiCard" key={item.key}>
                                <span>{item.label}</span>
                                <strong>{item.value}</strong>
                            </article>
                        ))}
                    </div>

                    {rol === "1" && (
                        <div className="warrantyFiltersGrid blockBody">
                            <label className="warrantyField">
                                <span>Buscar garantía</span>
                                <input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="DNI, nombre, teléfono, IMEI o sede"
                                />
                            </label>

                            <label className="warrantyField">
                                <span>Asesor</span>
                                <Select
                                    classNamePrefix="warrantySelect"
                                    value={advisorFilter}
                                    onChange={setAdvisorFilter}
                                    options={advisorOptions}
                                    placeholder=""
                                    isClearable
                                />
                            </label>

                            <label className="warrantyField ">
                                <span>Estado</span>
                                <Select
                                    classNamePrefix="warrantySelect"
                                    value={statusFilter}
                                    onChange={setStatusFilter}
                                    options={STATUS_OPTIONS}
                                    placeholder=""
                                />
                            </label>

                            <label className="warrantyField">
                                <span>Fecha de ingreso</span>
                                <input ref={rangeRef} placeholder="Seleccionar rango" />
                            </label>
                        </div>
                    )}

                    <div className="warrantyTableCard blockBody">
                        <div className="warrantyTableHead">
                            <h2>Lista de garantías</h2>
                            <span>
                                {loading ? "Cargando..." : `${warranties.length} registros`}
                            </span>
                        </div>

                        <div className="warrantyTableWrap">
                            <table className="warrantyTable">
                                <thead>
                                    <tr>
                                        <th>Código</th>
                                        <th>Cliente</th>
                                        <th>Equipo dañado</th>
                                        <th>Equipo nuevo / devolución</th>
                                        <th>Asesor de venta / Sede</th>
                                        <th>Registrado por</th>
                                        <th>Ingreso</th>
                                        <th>Estado</th>
                                        <th>Acciones</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {!warranties.length && !loading ? (
                                        <tr>
                                            <td colSpan="9" className="emptyCell">
                                                No hay garantías con los filtros seleccionados.
                                            </td>
                                        </tr>
                                    ) : (
                                        warranties.map((w) => (
                                            <tr key={w.id}>
                                                <td>
                                                    <b>{w.warranty_code}</b>
                                                    <small>DNI: {w.dni}</small>
                                                </td>

                                                <td>
                                                    <b>{w.client_name}</b>
                                                    <small>{w.phone_number || "-"}</small>
                                                </td>

                                                <td className="warrantyDeviceCell warrantyDeviceDamaged">
                                                    <b>{w.phone_name || "-"}</b>
                                                    <small>IMEI dañado: {w.imei || "-"}</small>
                                                </td>

                                                <td className="warrantyDeviceCell warrantyDeviceReplacement">
                                                    {Number(w.status) === 4 ? (
                                                        <>
                                                            <b>
                                                                {w.replacement_phone_name ||
                                                                    "Nuevo equipo pendiente"}
                                                            </b>
                                                            <small>
                                                                IMEI nuevo: {w.replacement_imei || "-"}
                                                            </small>
                                                        </>
                                                    ) : Number(w.status) === 5 ? (
                                                        <>
                                                            <b>Devolución aprobada</b>
                                                            <small>
                                                                Monto: {formatMoney(w.refund_amount)}
                                                            </small>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <b>No aplica</b>
                                                            <small>Sin cambio registrado</small>
                                                        </>
                                                    )}
                                                </td>

                                                <td>
                                                    <b>{w.advisor_name || "Asesor no disponible"}</b>
                                                    <small>Sede: {w.sede_name || "-"}</small>
                                                </td>

                                                <td>
                                                    <b>{w.created_by_name || "Usuario no disponible"}</b>
                                                    <small>Recepcionó la garantía</small>
                                                </td>

                                                <td>{formatDate(w.received_date)}</td>

                                                <td>
                                                    <span
                                                        className={`warrantyStatus ${statusClass(w.status)}`}
                                                    >
                                                        {w.status_label}
                                                    </span>
                                                </td>

                                                <td>
                                                    <div className="warrantyActions">
                                                        <button
                                                            type="button"
                                                            onClick={() => openStatusModal(w)}
                                                        >
                                                            Estado
                                                        </button>

                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                w.pdf_file
                                                                    ? window.open(
                                                                        process.env.REACT_APP_API + w.pdf_file,
                                                                        "_blank",
                                                                    )
                                                                    : regeneratePdf(w)
                                                            }
                                                        >
                                                            PDF
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </section>

            {showModal && (
                <div className="warrantyModalOverlay">
                    <div className="warrantyModal">
                        <div className="warrantyModalHead">
                            <div>
                                <h2>Nueva garantía</h2>
                                <p>Busca la venta por DNI y registra el ingreso del equipo.</p>
                            </div>

                            <button type="button" onClick={() => setShowModal(false)}>
                                ×
                            </button>
                        </div>

                        <div className="warrantySearchSale">
                            <label className="warrantyField">
                                <span>DNI del cliente</span>
                                <input
                                    value={dniSearch}
                                    onChange={(e) =>
                                        setDniSearch(e.target.value.replace(/\D/g, ""))
                                    }
                                    placeholder="Ej: 43302315"
                                    maxLength={12}
                                />
                            </label>

                            <button
                                className="warrantyPrimaryBtn"
                                type="button"
                                onClick={searchSaleByDni}
                                disabled={saleLoading}
                            >
                                {saleLoading ? "Buscando..." : "Buscar venta"}
                            </button>
                        </div>

                        {!!salesFound.length && (
                            <div className="saleResultGrid">
                                {salesFound.map((sale) => (
                                    <button
                                        type="button"
                                        key={sale.registerID}
                                        className={`saleResultCard ${selectedSale?.registerID === sale.registerID
                                                ? "active"
                                                : ""
                                            } ${Number(sale.warranty_state) === 2 ? "disabledSale" : ""
                                            }`}
                                        onClick={() => setSelectedSale(sale)}
                                    >
                                        <strong>{sale.client_name}</strong>
                                        <span>{sale.sale_type_label}</span>
                                        <small>
                                            {sale.phone || "-"} · IMEI {sale.imei || "-"}
                                        </small>
                                        <small>
                                            Precio {formatMoney(sale.phone_price)} · Pagado{" "}
                                            {formatMoney(sale.paid_amount)}
                                        </small>
                                    </button>
                                ))}
                            </div>
                        )}

                        {selectedSale && (
                            <div className="warrantyFormGrid">
                                <label className="warrantyField warrantyFieldFull">
                                    <span>
                                        Razones de garantía <b>*</b>
                                    </span>

                                    <Select
                                        classNamePrefix="warrantySelect"
                                        isMulti
                                        value={selectedReasons}
                                        onChange={(value) => setSelectedReasons(value || [])}
                                        options={WARRANTY_REASON_OPTIONS}
                                        placeholder="Seleccionar fallas reportadas"
                                    />
                                </label>

                                <label className="warrantyField">
                                    <span>Fecha estimada de devolución</span>
                                    <input
                                        ref={estimatedDateRef}
                                        placeholder="Seleccionar fecha de devolución"
                                    />
                                </label>

                                <label className="warrantyField warrantyFieldFull">
                                    <span>Descripción adicional</span>
                                    <textarea
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        placeholder="Detalle opcional: cuándo empezó la falla, accesorios entregados, condición física, etc."
                                    />
                                </label>
                            </div>
                        )}

                        <div className="warrantyModalFoot">
                            <button type="button" onClick={() => setShowModal(false)}>
                                Cancelar
                            </button>

                            <button
                                className="warrantyPrimaryBtn"
                                type="button"
                                onClick={saveWarranty}
                                disabled={saving}
                            >
                                {saving ? "Guardando..." : "Guardar y generar PDF"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {statusModal && selectedWarranty && (
                <div className="warrantyModalOverlay">
                    <div className="warrantyModal warrantyModalSmall">
                        <div className="warrantyModalHead">
                            <div>
                                <h2>Cambiar estado</h2>
                                <p>
                                    {selectedWarranty.warranty_code} ·{" "}
                                    {selectedWarranty.client_name}
                                </p>
                            </div>

                            <button type="button" onClick={() => setStatusModal(false)}>
                                ×
                            </button>
                        </div>

                        <div className="warrantyFormGrid oneColumn">
                            <label className="warrantyField">
                                <span>Nuevo estado</span>

                                <Select
                                    classNamePrefix="warrantySelect"
                                    value={newStatus}
                                    onChange={setNewStatus}
                                    options={STATUS_OPTIONS.filter((s) => s.value !== "0")}
                                />
                            </label>

                            {String(newStatus?.value) === "4" && (
                                <>
                                    <div className="warrantyExchangeResume warrantyFieldFull">
                                        <div>
                                            <span>Equipo dañado</span>
                                            <strong>{selectedWarranty.phone_name || "-"}</strong>
                                            <small>IMEI: {selectedWarranty.imei || "-"}</small>
                                        </div>
                                        <div>
                                            <span>Cliente / DNI</span>
                                            <strong>{selectedWarranty.client_name || "-"}</strong>
                                            <small>DNI: {selectedWarranty.dni || "-"}</small>
                                        </div>
                                        <div>
                                            <span>Asesor / Sede</span>
                                            <strong>{selectedWarranty.advisor_name || "-"}</strong>
                                            <small>{selectedWarranty.sede_name || "-"}</small>
                                        </div>
                                    </div>

                                    <label className="warrantyField">
                                        <span>Nuevo equipo / modelo</span>
                                        <Select
                                            classNamePrefix="warrantySelect"
                                            value={exchangePhoneOption}
                                            onMenuOpen={() => {
                                                if (!phoneSelectOptions.length) fetchPhones();
                                            }}
                                            onChange={(option) => {
                                                setExchangePhoneOption(option || null);
                                                setExchangePhone(option?.label || "");
                                                setExchangePrice(option?.price || "");
                                            }}
                                            options={phoneSelectOptions}
                                            placeholder="Seleccionar modelo"
                                            noOptionsMessage={() =>
                                                "No hay modelos cargados. Verifica la ruta /warranties/phones."
                                            }
                                            loadingMessage={() => "Cargando modelos..."}
                                            isClearable
                                            menuPortalTarget={
                                                typeof document !== "undefined" ? document.body : null
                                            }
                                            menuPosition="fixed"
                                            styles={{
                                                menuPortal: (base) => ({ ...base, zIndex: 10000 }),
                                            }}
                                        />
                                    </label>

                                    <label className="warrantyField">
                                        <span>IMEI del nuevo equipo</span>
                                        <input
                                            value={exchangeImei}
                                            onChange={(e) =>
                                                setExchangeImei(e.target.value.replace(/\D/g, ""))
                                            }
                                            placeholder="15 dígitos"
                                            maxLength={15}
                                        />
                                    </label>

                                    <label className="warrantyField">
                                        <span>Precio del nuevo equipo</span>
                                        <input
                                            type="number"
                                            value={exchangePrice}
                                            onChange={(e) => setExchangePrice(e.target.value)}
                                            placeholder="0.00"
                                        />
                                    </label>
                                </>
                            )}

                            {String(newStatus?.value) === "5" && (
                                <label className="warrantyField">
                                    <span>Monto a devolver</span>
                                    <input
                                        type="number"
                                        value={refundAmount}
                                        onChange={(e) => setRefundAmount(e.target.value)}
                                        placeholder="0.00"
                                    />
                                </label>
                            )}

                            <label className="warrantyField">
                                <span>Nota del estado</span>
                                <textarea
                                    value={statusNote}
                                    onChange={(e) => setStatusNote(e.target.value)}
                                    placeholder="Ej: Se reemplazó pantalla, se aprobó devolución, se comunicó al cliente, etc."
                                />
                            </label>
                        </div>

                        <div className="warrantyModalFoot">
                            <button type="button" onClick={() => setStatusModal(false)}>
                                Cancelar
                            </button>

                            <button
                                className="warrantyPrimaryBtn"
                                type="button"
                                onClick={updateStatus}
                                disabled={saving}
                            >
                                {saving
                                    ? "Actualizando..."
                                    : String(newStatus?.value) === "4"
                                        ? "Registrar cambio"
                                        : "Actualizar estado"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
